<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <!-- toolbar -->
        <v-skeleton-loader v-if="retrieveLoader" transition="fade-transition" type="toolbar" />
        <v-toolbar v-else color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">
            <v-row align="center" justify="start" no-gutters>
              <router-link :to="{ name: 'AgentList' }" class="subtitle-1 routerLink font-weight-medium">
                Agentes
              </router-link>
              <v-icon class="mx-1" color="secondary" small>mdi-chevron-right</v-icon>
              <span class="subtitle-1 font-weight-medium d-inline-block text-truncate" style="max-width:200px;">
                {{ agent.name }}
              </span>
            </v-row>
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="mr-2" v-if="agent.status === 'draft' || agent.status === 'paused'" @click="setStatus('active')" color="primary" :loading="statusLoader" outlined>
                <v-icon left small>mdi-play-circle-outline</v-icon>Activar
              </v-btn>
              <v-btn class="mr-2" v-if="agent.status === 'active'" @click="setStatus('paused')" :loading="statusLoader" outlined>
                <v-icon left small>mdi-pause-circle-outline</v-icon>Pausar
              </v-btn>
              <v-menu close-on-click close-on-content-click offset-y left :nudge-bottom="10" transition="slide-y-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn min-width="34" max-width="34" outlined color="secondary" v-bind="attrs" v-on="on">
                    <v-icon color="secondary">mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list class="pa-0">
                  <v-list-item class="py-1" @click="setStatus('archived')">
                    <v-list-item-title class="subtitle-2 secondary--text font-weight-medium">
                      <v-icon color="secondary" left small>mdi-archive-outline</v-icon>Archivar
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />

        <!-- agent header -->
        <v-card-text v-if="!retrieveLoader" class="pa-0">
          <v-row class="mx-0 pa-4 background" align="center" no-gutters>
            <v-avatar class="mr-4 outlined" size="62">
              <img :alt="agent.name" :src="require(`@/assets/appAsset-agents-${agent.avatar}.jpg`)" />
            </v-avatar>
            <div>
              <span class="body-1 text--secondary font-weight-medium">{{ agent.name }}</span>
              <div class="d-flex align-center mt-1">
                <v-chip x-small label class="mr-2" :color="agent.kind === 'collections' ? 'indigo lighten-5' : agent.kind === 'billing' ? 'teal lighten-5' : 'grey lighten-3'" :text-color="agent.kind === 'collections' ? 'indigo darken-2' : agent.kind === 'billing' ? 'teal darken-2' : 'grey darken-1'">
                  {{ kindLabel(agent.kind) }}
                </v-chip>
                <v-chip x-small label :color="statusColor(agent.status).bg" :text-color="statusColor(agent.status).text">
                  {{ statusLabel(agent.status) }}
                </v-chip>
                <span class="ml-3 body-2 text--disabled">
                  {{ autonomyLabel(agent.default_autonomy) }} · {{ activeModulesCount }} tarea{{ activeModulesCount !== 1 ? 's' : '' }} activa{{ activeModulesCount !== 1 ? 's' : '' }} · {{ agent.created | date }}
                </span>
              </div>
            </div>
          </v-row>
          <v-divider />
        </v-card-text>
        <!-- end agent header -->

        <!-- tabs -->
        <v-card-text class="pa-0">
          <v-row align="center" justify="space-between" no-gutters>
            <div>
              <v-tabs v-model="activeTab" color="primary" height="40" slider-size="2">
                <v-tab class="text-none font-weight-medium" :ripple="false">Actividad</v-tab>
                <v-tab class="text-none font-weight-medium" :ripple="false">
                  Insights
                  <v-badge v-if="newInsightsCount" :content="newInsightsCount" color="red" class="ml-2" inline />
                </v-tab>
                <v-tab class="text-none font-weight-medium" :ripple="false">Instrucciones</v-tab>
              </v-tabs>
            </div>
            <v-btn v-if="activeTab !== 2" class="mx-4" @click="refreshActive" :loading="boardLoading || insightsLoading" icon small><v-icon small>mdi-refresh</v-icon></v-btn>
          </v-row>
        </v-card-text>
        <!-- end tabs -->

        <v-divider />

        <v-tabs-items v-model="activeTab" class="transparent">
          <!-- activity tab -->
          <v-tab-item transition="fade-transition" reverse-transition="fade-transition">
            <v-card-text class="pa-0">
              <v-skeleton-loader v-if="boardLoading && !boardLoaded" type="image" height="400" />
              <div v-else class="agent-kanban">
                <v-card v-for="column in boardColumns" :key="column.key" class="ma-4 mr-0 agent-kanban-column" flat :color="column.color">
                  <v-toolbar class="agent-kanban-header pt-1" color="transparent" dense flat height="44">
                    <span class="body-2 text--secondary font-weight-medium text-uppercase">{{ column.label }}</span>
                    <v-chip class="ml-2" color="defaultGrey" label small text-color="secondary">{{ column.items.length }}</v-chip>
                  </v-toolbar>
                  <div class="agent-kanban-body pa-2 pt-0">
                    <span v-if="!column.items.length" class="d-block text-center caption text--secondary py-6">Sin registros</span>
                    <v-hover v-for="card in column.items" :key="card.uid" v-slot="{ hover }">
                      <v-card class="agent-kanban-card mt-2" :class="{ 'elevation-2': hover }" outlined @click="openActivity(card)">
                        <v-toolbar flat dense color="transparent" height="34">
                          <v-chip class="mt-5 mb-2 ml-n1 pr-3 pl-1" :color="card.statusStyle.color" label :text-color="card.statusStyle.textColor" small>
                            <v-icon class="mx-0" size="36" style="max-width: 18px;">mdi-circle-small</v-icon>{{ card.statusStyle.label }}
                          </v-chip>
                        </v-toolbar>
                        <v-list-item class="px-3 py-1">
                          <v-list-item-content class="py-1">
                            <v-list-item-subtitle class="subtitle-2 font-weight-medium text--secondary">{{ card.title }}</v-list-item-subtitle>
                            <v-list-item-title v-if="card.entity || card.entityType" class="caption text--secondary">
                              <entity-label :entity="card.entity" :entity-type="card.entityType" :entity-id="card.entityId" compact />
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mx-3" />
                        <v-card-actions class="py-2 px-3">
                          <span v-if="card.dateValue" class="caption text--primary">
                            <v-icon class="mr-1" color="secondary" size="15">mdi-calendar</v-icon>{{ card.dateLabel }} {{ card.dateValue | dateTime }}
                          </span>
                        </v-card-actions>
                      </v-card>
                    </v-hover>
                  </div>
                </v-card>
              </div>
            </v-card-text>
          </v-tab-item>
          <!-- end activity tab -->

          <!-- insights tab -->
          <v-tab-item transition="fade-transition" reverse-transition="fade-transition">
            <v-card-text class="pa-0">
              <!-- filters -->
              <v-row class="px-4 py-3" align="center" no-gutters>
                <v-select v-model="insightStatus" :items="insightStatusItems" @change="loadInsights" label="Estado" dense outlined hide-details class="mr-3" style="max-width: 200px;" />
                <v-select v-model="insightSeverity" :items="insightSeverityItems" @change="loadInsights" label="Severidad" dense outlined hide-details clearable style="max-width: 200px;" />
              </v-row>
              <!-- end filters -->

              <v-divider />

              <div class="pa-4" style="height: calc(100vh - 378px); overflow-y: auto;">
                <v-skeleton-loader v-if="insightsLoading && !insightsLoaded" type="list-item-three-line, list-item-three-line" />
                <template v-else>
                  <div v-if="!insights.length" class="text-center py-10">
                    <v-icon size="48" color="grey lighten-1">mdi-lightbulb-outline</v-icon>
                    <div class="mt-3 body-2 text--secondary">Sin insights</div>
                  </div>

                  <v-hover v-for="ins in insights" v-slot="{ hover }" :key="ins.id">
                    <v-card class="mb-3 agent-insight-card" :class="{ 'elevation-2': hover }" outlined @click="openInsight(ins)">
                      <v-list-item class="py-2">
                        <v-list-item-content class="py-1">
                          <div class="d-flex align-center mb-1">
                            <v-chip class="mr-2" :color="severityStyle(ins.severity).color" :text-color="severityStyle(ins.severity).textColor" label small>
                              <v-icon left small>{{ severityStyle(ins.severity).icon }}</v-icon>{{ severityStyle(ins.severity).label }}
                            </v-chip>
                            <v-chip color="defaultGrey" text-color="secondary" label x-small>{{ insightTypeLabel(ins.insight_type) }}</v-chip>
                            <v-spacer />
                            <span class="caption text--disabled">{{ ins.generated_at | dateTime }}</span>
                          </div>
                          <v-list-item-title class="body-2 text--secondary font-weight-medium">{{ ins.title }}</v-list-item-title>
                          <v-list-item-subtitle class="body-2 text--primary mt-1" style="white-space: normal;">{{ ins.summary }}</v-list-item-subtitle>
                          <div v-if="ins.entity || ins.entity_type" class="caption text--secondary mt-1">
                            <entity-label :entity="ins.entity" :entity-type="ins.entity_type" :entity-id="ins.entity_id" compact />
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card>
                  </v-hover>
                </template>
              </div>
            </v-card-text>
          </v-tab-item>
          <!-- end insights tab -->

          <!-- briefing tab -->
          <v-tab-item transition="fade-transition" reverse-transition="fade-transition">
            <v-card-text class="pa-6" style="height: calc(100vh - 370px); overflow-y: auto;">
              <div class="fill-height">
                <v-skeleton-loader v-if="retrieveLoader" type="table-tbody" />

                <template v-else>
                  <!-- identity -->
                  <div class="subtitle-1 text--secondary font-weight-medium">¿Quién soy?</div>
                  <div class="body-2 text--primary mb-4">Define la identidad y el propósito de este agente.</div>

                  <v-card outlined class="mb-8">
                    <v-card-text>
                      <v-row align="start">
                        <v-col cols="auto">
                          <span class="d-block mb-2 body-2 text--secondary font-weight-medium">Avatar</span>
                          <v-menu offset-y :close-on-content-click="false" transition="slide-y-transition">
                            <template v-slot:activator="{ on, attrs }">
                              <div v-bind="attrs" v-on="on" class="agent-avatar-picker">
                                <v-avatar class="outlined" size="56">
                                  <img :alt="editableAvatarCfg.label" :src="require(`@/assets/appAsset-agents-${editableAgent.avatar}.jpg`)" />
                                </v-avatar>
                                <v-icon class="agent-avatar-edit" color="secondary" size="18">mdi-pencil-circle</v-icon>
                              </div>
                            </template>
                            <v-card class="pa-3" width="276">
                              <span class="d-block mb-2 caption text--secondary font-weight-medium">Elige un avatar</span>
                              <v-row dense>
                                <v-col cols="auto" v-for="av in avatarOptions" :key="av.value">
                                  <v-avatar size="40" style="cursor: pointer; transition: all 0.15s;" :class="{ 'avatar-selected': editableAgent.avatar === av.value }" @click="editableAgent.avatar = av.value">
                                    <img :alt="av.label" :src="require(`@/assets/appAsset-agents-${av.value}.jpg`)" />
                                  </v-avatar>
                                </v-col>
                              </v-row>
                            </v-card>
                          </v-menu>
                        </v-col>
                        <v-col>
                          <span class="d-block mb-1 body-2 text--secondary font-weight-medium">Nombre</span>
                          <v-text-field v-model="editableAgent.name" class="mb-4" outlined dense hide-details maxlength="64" placeholder="Nombre del agente" />
                          <span class="d-block mb-1 body-2 text--secondary font-weight-medium">Descripción</span>
                          <v-textarea v-model="editableAgent.description" outlined dense hide-details rows="2" no-resize maxlength="256" placeholder="Describe qué hace este agente" />
                          <div class="mt-3">
                            <v-chip x-small label class="mr-2" :color="agent.kind === 'collections' ? 'indigo lighten-5' : agent.kind === 'billing' ? 'teal lighten-5' : 'grey lighten-3'" :text-color="agent.kind === 'collections' ? 'indigo darken-2' : agent.kind === 'billing' ? 'teal darken-2' : 'grey darken-1'">
                              {{ kindLabel(agent.kind) }}
                            </v-chip>
                            <v-chip x-small label color="defaultGrey" text-color="secondary">
                              {{ autonomyLabel(agent.default_autonomy) }}
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                  <!-- end identity -->

                  <!-- tasks definition -->
                  <div class="subtitle-1 text--secondary font-weight-medium">¿Qué debo hacer?</div>
                  <div class="body-2 text--primary mb-4">Activa las tareas que este agente debe ejecutar y define el nivel de autonomía con el que puede actuar en cada una.</div>

                  <v-card v-for="mod in editableModules" :key="`do-${mod.id}`" outlined class="mb-3">
                    <v-list-item class="py-0">
                      <v-list-item-action class="mr-3">
                        <v-switch v-model="mod.is_enabled" inset color="primary" hide-details dense />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="body-2 text--secondary font-weight-medium" :class="{ 'text--disabled': !mod.is_enabled }">{{ taskTitle(mod.module_key) }}</v-list-item-title>
                        <v-list-item-subtitle class="body-2 text--primary text-truncate">{{ taskDescription(mod.module_key) }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action style="width: 280px;">
                        <v-select v-model="mod.autonomy" :items="autonomyItems" :disabled="!mod.is_enabled" dense outlined hide-details class="body-2">
                          <template v-slot:prepend>
                            <span class="my-1 body-2 text--secondary font-weight-medium">Autonomía</span>
                          </template>
                        </v-select>
                      </v-list-item-action>
                    </v-list-item>
                  </v-card>
                  <!-- end tasks definition -->
                </template>
              </div>
            </v-card-text>

            <v-divider />

            <v-card-actions class="pa-4">
              <v-spacer />
              <v-btn @click="saveInstructions" color="primary" :loading="saveLoader">Guardar cambios</v-btn>
            </v-card-actions>
          </v-tab-item>
          <!-- end briefing tab -->

        </v-tabs-items>
      </v-card>
    </v-col>

    <!-- task detail -->
    <v-navigation-drawer class="elevation-8" v-model="drawerActivity" fixed hide-overlay right temporary style="height: calc(100vh - 64px); top: 64px;" width="640">
      <v-card v-if="selectedActivity">
        <v-toolbar flat height="56">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">{{ selectedActivity.title }}</v-toolbar-title>

          <v-spacer />

          <v-toolbar-items v-if="selectedActivity.kind === 'approval'">
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="mr-2 align-self-center" @click="resolveSelected('rejected')" color="secondary" :disabled="resolveLoader === 'approved'" :loading="resolveLoader === 'rejected'" outlined>Rechazar</v-btn>
              <v-btn class="align-self-center" @click="resolveSelected('approved')" color="primary" depressed :disabled="resolveLoader === 'rejected'" :loading="resolveLoader === 'approved'"><v-icon left small>mdi-check</v-icon>Aprobar</v-btn>
            </v-row>
          </v-toolbar-items>

          <v-toolbar-items v-else-if="selectedActivity.kind === 'insight' && selectedActivity.insight.status === 'new'">
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="mr-2 align-self-center" @click="acknowledgeSelected('dismissed')" color="secondary" :disabled="ackLoader === 'acknowledged'" :loading="ackLoader === 'dismissed'" outlined>Descartar</v-btn>
              <v-btn class="align-self-center" @click="acknowledgeSelected('acknowledged')" color="primary" depressed :disabled="ackLoader === 'dismissed'" :loading="ackLoader === 'acknowledged'"><v-icon left small>mdi-check</v-icon>Reconocer</v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>

        <v-divider />

        <!-- status -->
        <v-card-text>
          <v-row class="white" no-gutters>
            <v-chip :color="selectedActivity.statusStyle.color" :text-color="selectedActivity.statusStyle.textColor" label small>
              {{ selectedActivity.statusStyle.label }}
            </v-chip>
            <v-spacer />
            <span v-if="selectedActivity.dateValue" class="caption text--secondary">
              {{ selectedActivity.dateLabel }} {{ selectedActivity.dateValue | dateTime }}
            </span>
          </v-row>
        </v-card-text>
        <!-- end status -->

        <v-divider />

        <v-card-text class="background overflow-y-auto" style="height: calc(100vh - 176px)">
          <div class="fill-height">
            <!-- APPROVAL content -->
            <template v-if="selectedActivity.kind === 'approval'">
              <v-row class="mx-2" no-gutters>
                <v-list class="v-list-form transparent">
                  <v-list-item>
                    <v-list-item-title class="py-2 subtitle-2 text--secondary font-weight-medium">Decisión</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Tarea</v-list-item-title>
                    <v-list-item-subtitle>{{ taskTitle(selectedActivity.decision.module_key) || '—' }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Tipo de decisión</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedActivity.decision.decision_type || '—' }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Entidad</v-list-item-title>
                    <v-list-item-subtitle>
                      <entity-label
                        v-if="selectedActivity.decision.entity || selectedActivity.decision.entity_type"
                        :entity="selectedActivity.decision.entity"
                        :entity-type="selectedActivity.decision.entity_type"
                        :entity-id="selectedActivity.decision.entity_id"
                      />
                      <span v-else>—</span>
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Justificación</v-list-item-title>
                    <v-list-item-subtitle style="white-space: normal;">{{ selectedActivity.decision.rationale || 'Sin justificación disponible' }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-row>

              <!-- proposed actions -->
              <template v-if="selectedProposedActions.length">
                <v-divider class="mx-2 my-4" />

                <v-row class="mx-2" no-gutters>
                  <div class="d-block mb-2">
                    <span class="subtitle-2 text--secondary font-weight-medium">Acciones propuestas</span>
                  </div>
                  <v-sheet v-for="(pa, i) in selectedProposedActions" :key="`pa-${i}`" outlined rounded width="100%">
                    <div class="d-flex pa-2 pb-0 align-center">
                      <v-chip class="mr-2" color="defaultGrey" label text-color="secondary" x-small>{{ actionTypeLabel(pa.action_type) }}</v-chip>
                      <entity-label v-if="pa.entity || pa.entity_type" :entity="pa.entity" :entity-type="pa.entity_type" :entity-id="pa.entity_id" compact />
                    </div>
                    <pre v-if="hasData(pa.payload)" class="ma-2 agent-json">{{ pretty(pa.payload) }}</pre>
                  </v-sheet>
                </v-row>
              </template>
              <!-- end proposed actions -->

              <!-- signals -->
              <template v-if="hasData(selectedActivity.decision.input_payload)">
                <v-divider class="mx-2 my-4" />

                <v-row class="mx-2" no-gutters>
                  <div class="d-block mb-2">
                    <span class="subtitle-2 text--secondary font-weight-medium">Señales consideradas</span>
                  </div>
                  <v-sheet outlined rounded width="100%">
                    <pre class="ma-2 agent-json">{{ pretty(selectedActivity.decision.input_payload) }}</pre>
                  </v-sheet>
                </v-row>
              </template>
              <!-- end signals -->
            </template>
            <!-- end APPROVAL content -->

            <!-- INSIGHT content -->
            <template v-else-if="selectedActivity.kind === 'insight'">
              <agent-insight-body :insight="selectedActivity.insight" />
            </template>
            <!-- end INSIGHT content -->

            <!-- ACTION content -->
            <template v-else>
              <v-row class="mx-2" no-gutters>
                <v-list class="v-list-form transparent">
                  <v-list-item>
                    <v-list-item-title class="py-2 subtitle-2 text--secondary font-weight-medium">Acción</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Tipo</v-list-item-title>
                    <v-list-item-subtitle>{{ actionTypeLabel(selectedActivity.action.action_type) }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Estado</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedActivity.statusStyle.label }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Entidad</v-list-item-title>
                    <v-list-item-subtitle>
                      <entity-label
                        v-if="selectedActivity.action.entity || selectedActivity.action.entity_type"
                        :entity="selectedActivity.action.entity"
                        :entity-type="selectedActivity.action.entity_type"
                        :entity-id="selectedActivity.action.entity_id"
                      />
                      <span v-else>—</span>
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedActivity.action.scheduled_for" class="py-0">
                    <v-list-item-title>Programada para</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedActivity.action.scheduled_for | dateTime }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedActivity.action.enqueued_at" class="py-0">
                    <v-list-item-title>Encolada el</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedActivity.action.enqueued_at | dateTime }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedActivity.action.executed_at" class="py-0">
                    <v-list-item-title>Ejecutada el</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedActivity.action.executed_at | dateTime }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="py-0">
                    <v-list-item-title>Creada el</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedActivity.action.created | dateTime }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedActivity.action.idempotency_key" class="py-0">
                    <v-list-item-title>Idempotency key</v-list-item-title>
                    <v-list-item-subtitle class="text-truncate">{{ selectedActivity.action.idempotency_key }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-row>

              <template v-if="hasData(selectedActivity.action.payload)">
                <v-divider class="mx-2 my-4" />

                <v-row class="mx-2" no-gutters>
                  <div class="d-block mb-2">
                    <span class="subtitle-2 text--secondary font-weight-medium">Payload</span>
                  </div>
                  <v-sheet outlined rounded width="100%">
                    <pre class="ma-2 agent-json">{{ pretty(selectedActivity.action.payload) }}</pre>
                  </v-sheet>
                </v-row>
              </template>
              <template v-if="hasData(selectedActivity.action.result_payload)">
                <v-divider class="mx-2 my-4" />

                <v-row class="mx-2" no-gutters>
                  <div class="d-block mb-2">
                    <span class="subtitle-2 text--secondary font-weight-medium">Resultado</span>
                  </div>
                  <v-sheet outlined rounded width="100%">
                    <pre class="ma-2 agent-json">{{ pretty(selectedActivity.action.result_payload) }}</pre>
                  </v-sheet>
                </v-row>
              </template>
              <template v-if="hasData(selectedActivity.action.error_payload)">
                <v-divider class="mx-2 my-4" />

                <v-row class="mx-2" no-gutters>
                  <div class="d-block mb-2">
                    <span class="subtitle-2 red--text font-weight-medium">Error</span>
                  </div>
                  <v-sheet outlined rounded width="100%">
                    <pre class="ma-2 agent-json">{{ pretty(selectedActivity.action.error_payload) }}</pre>
                  </v-sheet>
                </v-row>
              </template>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
    <!-- end task detail -->

  </v-row>
</template>

<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'
import { mapState } from 'vuex'
import AgentInsightBody from '@/components/agents/AgentInsightBody'
import EntityLabel from '@/components/agents/EntityLabel'
import { agentAvatars, agentCollectionsTasks, agentBillingTasks, agentInsightSeverity, agentInsightTypeLabel, agentInsightStatusLabel } from '@/collections/agents'

const ALL_TASKS = [...agentCollectionsTasks, ...agentBillingTasks]

export default {
  components: {
    AgentInsightBody,
    EntityLabel
  },
  mixins: [
    GenericViewMixin
  ],
  data () {
    return {
      activeTab: 0,
      statusLoader: false,
      saveLoader: false,
      resolveLoader: null,
      editableModules: [],
      editableAgent: { name: '', description: '', avatar: null },
      avatarOptions: agentAvatars,
      boardLoading: false,
      boardLoaded: false,
      actions: [],
      approvals: [],
      decisions: [],
      drawerActivity: false,
      selectedActivity: null,
      insights: [],
      insightsLoading: false,
      insightsLoaded: false,
      insightStatus: 'new',
      insightSeverity: null,
      ackLoader: null,
      insightStatusItems: [
        { text: 'Nuevos', value: 'new' },
        { text: 'Reconocidos', value: 'acknowledged' },
        { text: 'Descartados', value: 'dismissed' },
        { text: 'Todos', value: null }
      ],
      insightSeverityItems: [
        { text: 'Informativo', value: 'info' },
        { text: 'Atención', value: 'warning' },
        { text: 'Crítico', value: 'critical' }
      ],
      autonomyItems: [
        { text: 'Solo sugerir', value: 'suggest_only' },
        { text: 'Con aprobación', value: 'approval_required' },
        { text: 'Automático', value: 'auto_execute' }
      ]
    }
  },
  computed: {
    ...mapState({
      agent: state => state.agents.agentsDetail,
      modulesList: state => state.agents.modulesList
    }),
    retrieveLoader () {
      return this.$store.state.agents.retrieveLoader
    },
    agentId () {
      return this.$route.params.id
    },
    avatarCfg () {
      return agentAvatars.find(a => a.value === this.agent.avatar) || agentAvatars[0]
    },
    editableAvatarCfg () {
      return agentAvatars.find(a => a.value === this.editableAgent.avatar) || agentAvatars[0]
    },
    pendingApprovalsCount () {
      return this.approvals.length
    },
    newInsightsCount () {
      return this.insights.filter(i => i.status === 'new').length
    },
    activeModulesCount () {
      return this.modulesList.filter(m => m.is_enabled).length
    },
    activityCards () {
      const cards = []

      // pending approvals → "esperando aprobación" (en progreso)
      this.approvals.forEach((a) => {
        const decision = this.decisions.find(d => d.id === a.decision) || {}
        cards.push({
          uid: `apr-${a.id}`,
          kind: 'approval',
          column: 'en_progreso',
          approval: a,
          decision,
          title: this.taskTitle(decision.module_key) || 'Aprobación pendiente',
          entity: decision.entity,
          entityType: decision.entity_type,
          entityId: decision.entity_id,
          statusStyle: { label: 'Esperando aprobación', color: 'amber lighten-5', textColor: 'amber darken-4' },
          dateLabel: 'Solicitado el',
          dateValue: a.created
        })
      })

      // actions → programado / en progreso / completado
      this.actions.forEach((act) => {
        const column = this.actionColumn(act)
        cards.push({
          uid: `act-${act.id}`,
          kind: 'action',
          column,
          action: act,
          title: this.actionTypeLabel(act.action_type, column),
          entity: act.entity,
          entityType: act.entity_type,
          entityId: act.entity_id,
          statusStyle: this.actionStatusStyle(act, column),
          dateLabel: column === 'programado' ? 'Programado para el' : (act.executed_at ? 'Ejecutado el' : 'Creado el'),
          dateValue: column === 'programado' ? act.scheduled_for : (act.executed_at || act.created)
        })
      })

      return cards
    },
    boardColumns () {
      const cards = this.activityCards
      return [
        { key: 'programado', label: 'Programado', color: '#FFFDF5', items: cards.filter(c => c.column === 'programado') },
        { key: 'en_progreso', label: 'En progreso', color: 'background', items: cards.filter(c => c.column === 'en_progreso') },
        { key: 'completado', label: 'Completado', color: '#F5FAF7', items: cards.filter(c => c.column === 'completado') }
      ]
    },
    selectedProposedActions () {
      if (!this.selectedActivity || this.selectedActivity.kind !== 'approval') return []
      const dp = this.selectedActivity.decision.decision_payload || {}
      if (Array.isArray(dp.proposed_actions)) return dp.proposed_actions
      if (dp.proposed_action) return [dp.proposed_action]
      return []
    }
  },
  watch: {
    modulesList (val) {
      this.editableModules = val.map(m => ({ ...m }))
    },
    agent (val) {
      this.editableAgent = {
        name: val.name || '',
        description: val.description || '',
        avatar: val.avatar || null
      }
    }
  },
  created () {
    const id = this.agentId
    this.$store.dispatch('agents/RETRIEVE', {
      resource: 'agents',
      id
    })
    this.$store.dispatch('agents/LIST', {
      resource: 'agents/modules',
      query: { agent: id },
      loader: false
    })
    this.loadBoard()
    this.loadInsights()
  },
  methods: {
    loadBoard () {
      const id = this.agentId
      this.boardLoading = true
      Promise.all([
        this.$store.dispatch('agents/LIST', { resource: 'agents/actions', query: { agent: id, page_size: 100, expand: 'entity' }, setState: false, loader: false }),
        this.$store.dispatch('agents/LIST', { resource: 'agents/approvals', query: { agent: id, status: 'pending', page_size: 100 }, setState: false, loader: false }),
        this.$store.dispatch('agents/LIST', { resource: 'agents/decisions', query: { agent: id, status: 'pending_approval', page_size: 100, expand: 'entity' }, setState: false, loader: false })
      ])
      .then(([actionsRes, approvalsRes, decisionsRes]) => {
        this.actions = actionsRes.data.results
        this.approvals = approvalsRes.data.results
        this.decisions = decisionsRes.data.results
        this.boardLoaded = true
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          this.$dialog.message.error(this.parseErrors(error.response.data))
        }
      })
      .finally(() => {
        this.boardLoading = false
      })
    },
    refreshActive () {
      if (this.activeTab === 1) {
        this.loadInsights()
      } else {
        this.loadBoard()
      }
    },
    loadInsights () {
      const id = this.agentId
      this.insightsLoading = true
      const query = { agent: id, page_size: 100, expand: 'entity' }
      if (this.insightStatus) query.status = this.insightStatus
      if (this.insightSeverity) query.severity = this.insightSeverity
      this.$store.dispatch('agents/LIST', { resource: 'agents/insights', query, setState: false, loader: false })
      .then((response) => {
        this.insights = response.data.results
        this.insightsLoaded = true
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          this.$dialog.message.error(this.parseErrors(error.response.data))
        }
      })
      .finally(() => {
        this.insightsLoading = false
      })
    },
    severityStyle (severity) {
      return agentInsightSeverity[severity] || agentInsightSeverity.info
    },
    insightTypeLabel (type) {
      return agentInsightTypeLabel[type] || 'Insight'
    },
    openInsight (insight) {
      const sev = this.severityStyle(insight.severity)
      const statusLabel = agentInsightStatusLabel[insight.status] || insight.status
      this.selectedActivity = {
        kind: 'insight',
        insight,
        title: insight.title,
        statusStyle: { label: `${sev.label} · ${statusLabel}`, color: sev.color, textColor: sev.textColor },
        dateLabel: 'Generado el',
        dateValue: insight.generated_at
      }
      this.drawerActivity = true
    },
    acknowledgeSelected (status) {
      if (!this.selectedActivity || this.selectedActivity.kind !== 'insight') return
      const insightId = this.selectedActivity.insight.id
      this.ackLoader = status
      this.$store.dispatch('agents/CREATE', {
        resource: 'agents/insights',
        detail: 'acknowledge',
        id: insightId,
        payload: { status },
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success(status === 'acknowledged' ? 'Insight reconocido' : 'Insight descartado')
        this.drawerActivity = false
        this.selectedActivity = null
        this.loadInsights()
      })
      .catch((error) => {
        const msg = error.response && error.response.data ? this.parseErrors(error.response.data) : null
        this.$dialog.message.error(msg || 'No se pudo procesar el insight')
      })
      .finally(() => {
        this.ackLoader = null
      })
    },
    actionColumn (act) {
      if (act.status === 'pending' && act.scheduled_for && new Date(act.scheduled_for).getTime() > Date.now()) {
        return 'programado'
      }
      if (act.status === 'pending' || act.status === 'processing') {
        return 'en_progreso'
      }
      return 'completado'
    },
    taskTitle (moduleKey) {
      const task = ALL_TASKS.find(t => t.task_key === moduleKey)
      return task ? task.title : moduleKey
    },
    taskDescription (moduleKey) {
      const task = ALL_TASKS.find(t => t.task_key === moduleKey)
      return task ? task.description : ''
    },
    actionTypeLabel (type, column) {
      // tense-aware label for the board: infinitive (programado),
      // gerund (en progreso) and past participle (completado).
      const tensed = {
        retry: { programado: 'Reintentar pago', en_progreso: 'Reintentando pago', completado: 'Pago reintentado' },
        contact: { programado: 'Contactar cliente', en_progreso: 'Contactando cliente', completado: 'Cliente contactado' },
        escalate: { programado: 'Escalar caso', en_progreso: 'Escalando caso', completado: 'Caso escalado' },
        custom: { programado: 'Ejecutar acción', en_progreso: 'Ejecutando acción', completado: 'Acción ejecutada' }
      }
      if (column && tensed[type] && tensed[type][column]) {
        return tensed[type][column]
      }
      // base noun (used in the detail drawer and proposed actions)
      const base = {
        retry: 'Reintento de pago',
        contact: 'Contacto al cliente',
        escalate: 'Escalación',
        custom: 'Acción personalizada'
      }
      return base[type] || type
    },
    actionStatusStyle (act, column) {
      if (column === 'programado') return { label: 'Programada', color: 'blue lighten-5', textColor: 'blue darken-2' }
      if (act.status === 'processing') return { label: 'Procesando', color: 'indigo lighten-5', textColor: 'indigo darken-2' }
      if (act.status === 'pending') return { label: 'En progreso', color: 'amber lighten-5', textColor: 'amber darken-4' }
      if (act.status === 'executed') return { label: 'Completada', color: 'green lighten-5', textColor: 'green darken-2' }
      if (act.status === 'failed') return { label: 'Fallida', color: 'red lighten-5', textColor: 'red darken-2' }
      if (act.status === 'cancelled') return { label: 'Cancelada', color: 'defaultGrey', textColor: 'secondary' }
      return { label: act.status, color: 'defaultGrey', textColor: 'secondary' }
    },
    openActivity (card) {
      this.selectedActivity = card
      this.drawerActivity = true
    },
    pretty (obj) {
      if (!this.hasData(obj)) return ''
      return JSON.stringify(obj, null, 2)
    },
    hasData (obj) {
      return obj && typeof obj === 'object' && Object.keys(obj).length > 0
    },
    kindLabel (kind) {
      const map = { collections: 'Cobranza', billing: 'Facturación', custom: 'Personalizado' }
      return map[kind] || kind
    },
    autonomyLabel (autonomy) {
      const map = {
        suggest_only: 'Solo sugerir',
        approval_required: 'Con aprobación',
        auto_execute: 'Automático'
      }
      return map[autonomy] || autonomy
    },
    statusColor (status) {
      const map = {
        active: { bg: 'green lighten-5', text: 'green darken-2' },
        paused: { bg: 'orange lighten-5', text: 'orange darken-2' },
        draft: { bg: 'grey lighten-3', text: 'grey darken-1' },
        archived: { bg: 'blue-grey lighten-5', text: 'blue-grey darken-1' }
      }
      return map[status] || { bg: 'grey lighten-3', text: 'grey darken-1' }
    },
    statusLabel (status) {
      const map = { active: 'Activo', paused: 'Pausado', draft: 'Borrador', archived: 'Archivado' }
      return map[status] || status
    },
    setStatus (newStatus) {
      this.statusLoader = true
      this.$store.dispatch('agents/UPDATE', {
        resource: 'agents',
        id: this.agentId,
        payload: { status: newStatus }
      })
      .then(() => {
        this.$dialog.message.success('Estado actualizado')
      })
      .catch(() => {
        this.$dialog.message.error('No se pudo actualizar el estado')
      })
      .finally(() => {
        this.statusLoader = false
      })
    },
    resolveSelected (status) {
      if (!this.selectedActivity || this.selectedActivity.kind !== 'approval') return
      const approvalId = this.selectedActivity.approval.id
      this.resolveLoader = status
      this.$store.dispatch('agents/CREATE', {
        resource: 'agents/approvals',
        detail: 'resolve',
        id: approvalId,
        payload: { status },
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success(status === 'approved' ? 'Aprobado' : 'Rechazado')
        this.drawerActivity = false
        this.selectedActivity = null
        this.loadBoard()
      })
      .catch((error) => {
        const msg = error.response && error.response.data ? this.parseErrors(error.response.data) : null
        this.$dialog.message.error(msg || 'No se pudo procesar la aprobación')
      })
      .finally(() => {
        this.resolveLoader = null
      })
    },
    saveInstructions () {
      this.saveLoader = true
      const agentUpdate = this.$store.dispatch('agents/UPDATE', {
        resource: 'agents',
        id: this.agentId,
        payload: {
          name: this.editableAgent.name,
          description: this.editableAgent.description,
          avatar: this.editableAgent.avatar
        }
      })
      const moduleUpdates = this.editableModules.map(mod =>
        this.$store.dispatch('agents/UPDATE', {
          resource: 'agents/modules',
          id: mod.id,
          payload: { is_enabled: mod.is_enabled, autonomy: mod.autonomy }
        })
      )
      Promise.all([agentUpdate, ...moduleUpdates])
        .then(() => {
          this.$dialog.message.success('Configuración guardada')
        })
        .catch((error) => {
          const msg = error.response && error.response.data ? this.parseErrors(error.response.data) : null
          this.$dialog.message.error(msg || 'No se pudieron guardar los cambios')
        })
        .finally(() => {
          this.saveLoader = false
        })
    }
  }
}
</script>
<style scoped>
.agent-kanban {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}
.agent-kanban-column {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 335px);
  width: 360px;
  min-width: 300px;
}
.agent-kanban-header {
  max-height: 44px;
}
.agent-kanban-body {
  flex: 1 1 auto;
  align-self: flex-start;
  min-height: 60px;
  overflow-x: auto;
  width: 100%;
}
.agent-kanban-card {
  cursor: pointer;
  transition: ease-in-out;
}
.agent-avatar-picker {
  position: relative;
  width: 56px;
  cursor: pointer;
}
.agent-avatar-edit {
  position: absolute;
  right: -2px;
  bottom: -2px;
  background: white;
  border-radius: 50%;
}
.avatar-selected {
  box-shadow: 0 0 0 3px #3F51B5;
}
.agent-json {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: #F7F8FA;
  border-radius: 4px;
  padding: 8px 10px;
  margin: 0;
  color: #5A6072;
}
</style>
